import { Loader2, StickyNote } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  PROVIDER_LABELS,
  STATUS_LABELS,
  shortIccid,
  simStatusKind,
  type SimCard,
  type SimProvider,
  type SimStatusKind,
} from "@/lib/sims";

const STATUS_VARIANT: Record<
  SimStatusKind,
  "success" | "warning" | "muted" | "destructive" | "secondary" | "outline"
> = {
  active: "success",
  idle: "secondary",
  suspended: "warning",
  deactivated: "muted",
  inventory: "outline",
  missing: "destructive",
  unknown: "outline",
};

export function ProviderBadge({ provider }: { provider: SimProvider }) {
  return (
    <Badge variant="outline" data-testid={`sim-provider-${provider}`}>
      {PROVIDER_LABELS[provider]}
    </Badge>
  );
}

export function SimStatusBadge({
  sim,
  pending,
}: {
  sim: Pick<SimCard, "status" | "missingSince" | "provider">;
  pending?: boolean;
}) {
  const kind = simStatusKind(sim);
  const label =
    kind === "idle" && sim.provider === "tele2"
      ? "Idle (activation ready)"
      : STATUS_LABELS[kind];
  return (
    <span className="inline-flex items-center gap-1.5">
      <Badge variant={STATUS_VARIANT[kind]} data-testid="sim-status">
        {label}
      </Badge>
      {pending && (
        <Loader2
          className="h-3.5 w-3.5 animate-spin text-muted-foreground"
          aria-label="Action in progress"
          data-testid="sim-pending"
        />
      )}
    </span>
  );
}

/** Full ICCID on desktop, last 8 digits on small screens. */
export function IccidText({ iccid }: { iccid: string }) {
  return (
    <>
      <span className="lg:hidden">{shortIccid(iccid)}</span>
      <span className="hidden whitespace-nowrap lg:inline">{iccid}</span>
    </>
  );
}

/**
 * Internal note in a SIM list: one muted, truncated line; the full text shows
 * on hover.
 */
export function SimNoteLine({
  note,
  testId = "sim-note",
}: {
  note: string | null;
  testId?: string;
}) {
  if (!note) return null;
  return (
    <div
      className="flex max-w-56 items-center gap-1 text-xs text-muted-foreground"
      title={note}
      data-testid={testId}
    >
      <StickyNote className="h-3 w-3 shrink-0" aria-label="Note" />
      <span className="truncate">{note}</span>
    </div>
  );
}
